import { useEffect, useState } from 'react';

import { ExamGridModel, FileModel, TagModel } from 'models';

import {
  CATEGORY_VALUE_TO_DYNAMIC_TAG_NAME,
  DYNAMIC_TAGS_START_ID,
  ROOT_CATEGORY_TAG_ID,
  ROOT_TAG_DEFINITIONS,
} from 'features/patient/services/dynamicTagHelper';
import { flattenTags } from 'features/patient/services/tagHelperUtils';

// Removed useTagLookups import as it wasn't used directly here

interface UseMergedTagsResult {
  mergedHierarchicalTags: TagModel[];
  mergedFlatTags: TagModel[];
  mergedTagNameToIdMap: Map<string, number>;
  mergedTagIdToTagMap: Map<number, TagModel>;
  isLoadingMergedTags: boolean;
}

interface UseMergedTagsProps {
  fetchedHierarchicalTags: TagModel[];
  fetchedTagNameToIdMap: Map<string, number>;
  fetchedTagIdToTagMap: Map<number, TagModel>;
  isLoadingTags: boolean;
}

export const useMergedTags = ({
  fetchedHierarchicalTags,
  fetchedTagNameToIdMap,
  fetchedTagIdToTagMap,
  isLoadingTags,
}: UseMergedTagsProps): UseMergedTagsResult => {
  const [mergedHierarchicalTags, setMergedHierarchicalTags] = useState<TagModel[]>([]);
  const [mergedFlatTags, setMergedFlatTags] = useState<TagModel[]>([]);
  const [mergedTagNameToIdMap, setMergedTagNameToIdMap] = useState<Map<string, number>>(new Map());
  const [mergedTagIdToTagMap, setMergedTagIdToTagMap] = useState<Map<number, TagModel>>(new Map());
  const [isSynthesizing, setIsSynthesizing] = useState(false);

  useEffect(() => {
    if (!isLoadingTags) {
      console.log('[useMergedTags] Starting synthesis...');
      setIsSynthesizing(true);
      const dynamicTags: TagModel[] = [];
      let dynamicTagIdCounter = DYNAMIC_TAGS_START_ID;

      const dynamicParentMap = new Map<number, TagModel & { children: TagModel[] }>();
      ROOT_TAG_DEFINITIONS.forEach((p) => {
        dynamicParentMap.set(p.id, { ...p, children: [] });
      });

      const synthesizedTagNames = new Set<string>();

      // --- Category Synthesis (remains for now) ---
      Object.entries(CATEGORY_VALUE_TO_DYNAMIC_TAG_NAME).forEach(([_, tagName]) => {
        const lowerTagName = tagName.toLowerCase();
        if (!fetchedTagNameToIdMap.has(lowerTagName) && !synthesizedTagNames.has(lowerTagName)) {
          const dynamicTag: TagModel = {
            id: dynamicTagIdCounter--,
            name: tagName,
            parentTagId: ROOT_CATEGORY_TAG_ID,
            active: true,
            children: [],
          };
          dynamicTags.push(dynamicTag);
          const parent = dynamicParentMap.get(ROOT_CATEGORY_TAG_ID);
          if (parent) parent.children.push(dynamicTag);
          synthesizedTagNames.add(lowerTagName);
        }
      });

      // --- Combine and Finalize ---
      const dynamicParentsToAdd = Array.from(dynamicParentMap.values()).filter((p) => p.children.length > 0);
      const finalHierarchical = [...fetchedHierarchicalTags, ...dynamicParentsToAdd];
      const finalFlat = flattenTags(finalHierarchical);
      const finalTagNameMap = new Map(fetchedTagNameToIdMap);
      const finalTagIdMap = new Map(fetchedTagIdToTagMap);

      finalFlat.forEach((tag) => {
        if (tag.id < 0 || synthesizedTagNames.has(tag.name.toLowerCase())) {
          finalTagNameMap.set(tag.name.toLowerCase(), tag.id);
          finalTagIdMap.set(tag.id, tag);
        }
      });

      setMergedHierarchicalTags(finalHierarchical);
      setMergedFlatTags(finalFlat);
      setMergedTagNameToIdMap(finalTagNameMap);
      setMergedTagIdToTagMap(finalTagIdMap);
      console.log('[useMergedTags] Synthesis complete with simplified dynamic tags.');
      setIsSynthesizing(false);
    }
  }, [
    isLoadingTags,
    fetchedHierarchicalTags,
    // Create stable keys for Maps to prevent infinite re-renders
    fetchedTagNameToIdMap.size,
    Array.from(fetchedTagNameToIdMap.keys()).sort().join(','),
    fetchedTagIdToTagMap.size,
    Array.from(fetchedTagIdToTagMap.keys()).sort().join(','),
  ]);

  return {
    mergedHierarchicalTags,
    mergedFlatTags,
    mergedTagNameToIdMap,
    mergedTagIdToTagMap,
    isLoadingMergedTags: isLoadingTags || isSynthesizing,
  };
};
